import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import usersService from "../../../services/UsersService";
import SwitchCom from "../../Helpers/SwitchCom";

// Same 3 sections/copy/endpoints as myfit-app's ConnectQRScreen: the QR
// code itself (+ regenerate), sharing preferences, and the list of
// accounts already connected via a past scan.
function formatCode(code) {
  return code.replace(/(.{4})(?=.)/g, "$1 ");
}

const SHARING_TOGGLES = [
  { key: "share_tracking_records", label: "Share Tracking Records" },
  { key: "allow_shared_content", label: "Allow Shared Contents" },
  { key: "allow_connect_notifications", label: "Allow Connect Notifications" },
];

const SHARING_DEFAULTS = {
  share_tracking_records: true,
  allow_shared_content: true,
  allow_connect_notifications: true,
};

function formatConnectionDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `Connected ${d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
}

export default function ConnectQRTab() {
  const api = new usersService();

  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sharing, setSharing] = useState(SHARING_DEFAULTS);
  const [sharingLoading, setSharingLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [sharingError, setSharingError] = useState(null);

  // "Accounts you have allowed connection" — accounts *this* user is
  // sending to (direction === "sending", i.e. the other side scanned
  // this account's own QR code). See _connection_dict() in app.py.
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionsError, setConnectionsError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadCode = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .createConnectCode()
      .then((res) => setCode(res.data.code))
      .catch(() => setError("Could not generate a code. Try again."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCode();
  }, [loadCode]);

  useEffect(() => {
    api
      .fetchConnectSharingPreferences()
      .then((res) => setSharing(res.data))
      .catch(() => setSharingError("Could not load sharing preferences."))
      .finally(() => setSharingLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSharing = async (key) => {
    const previous = sharing;
    const next = { ...sharing, [key]: !sharing[key] };
    setSharing(next);
    setSavingKey(key);
    setSharingError(null);
    try {
      await api.updateConnectSharingPreferences({ [key]: next[key] });
    } catch (err) {
      setSharing(previous);
      setSharingError("Could not save. Try again.");
    } finally {
      setSavingKey(null);
    }
  };

  useEffect(() => {
    api
      .fetchFamilyConnections()
      .then((res) => setConnections(res.data.connections.filter((c) => c.direction === "sending")))
      .catch(() => setConnectionsError("Could not load connections."))
      .finally(() => setConnectionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSuspend = async (connection) => {
    const nextSuspended = !connection.suspended;
    const previous = connections;
    setConnections((prev) =>
      prev.map((c) => (c.id === connection.id ? { ...c, suspended: nextSuspended } : c))
    );
    setTogglingId(connection.id);
    setConnectionsError(null);
    try {
      await api.suspendFamilyConnection(connection.id, nextSuspended);
    } catch (err) {
      setConnections(previous);
      setConnectionsError("Could not update this connection. Try again.");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="connect-qr-tab w-full">
      <div className="flex flex-col items-center text-center mb-8">
        <h1 className="text-xl tracking-wide font-bold text-dark-gray dark:text-white mb-2">
          Connect QR
        </h1>
        <p className="text-base text-thin-light-gray max-w-md mb-6">
          Scan this code with another device to connect it to your account.
        </p>

        <div className="w-[232px] h-[232px] rounded-2xl bg-white flex items-center justify-center mb-5 shadow-md">
          {loading ? (
            <span className="text-thin-light-gray text-sm">Loading…</span>
          ) : code ? (
            <QRCodeSVG value={code} size={200} bgColor="#ffffff" fgColor="#0f172a" />
          ) : null}
        </div>

        {error ? <p className="text-red-700 mb-4">{error}</p> : null}
        {code && !loading ? (
          <>
            <p className="text-lg tracking-widest text-dark-gray dark:text-white font-semibold mb-1">
              {formatCode(code)}
            </p>
            <p className="text-sm text-thin-light-gray mb-6">
              This code expires in a few minutes — tap to generate a new one.
            </p>
          </>
        ) : null}

        <button
          type="button"
          onClick={loadCode}
          disabled={loading}
          className="px-7 py-3 rounded-xl bg-purple text-white text-base font-semibold disabled:opacity-50"
        >
          Generate new code
        </button>
      </div>

      <h2 className="text-base font-bold text-dark-gray dark:text-white mb-1">What to share</h2>
      <p className="text-sm text-thin-light-gray mb-4">
        Choose what a connected device or person can see.
      </p>
      {sharingLoading ? (
        <p className="table-empty">Loading…</p>
      ) : (
        <ul className="w-full mb-2">
          {SHARING_TOGGLES.map((item, i) => (
            <li
              key={item.key}
              className={`flex justify-between items-center py-3.5 ${
                i === SHARING_TOGGLES.length - 1 ? "" : "border-b dark:border-[#5356fb29] border-light-purple"
              }`}
            >
              <span className="text-base text-dark-gray dark:text-white">{item.label}</span>
              {savingKey === item.key ? (
                <span className="text-sm text-thin-light-gray">Saving…</span>
              ) : (
                <SwitchCom value={sharing[item.key]} handler={() => toggleSharing(item.key)} />
              )}
            </li>
          ))}
        </ul>
      )}
      {sharingError ? <p className="text-red-700 mb-6">{sharingError}</p> : null}

      <h2 className="text-base font-bold text-dark-gray dark:text-white mt-6 mb-1">
        Accounts you have allowed connection
      </h2>
      <p className="text-sm text-thin-light-gray mb-4">Accounts you are sending to.</p>
      {connectionsLoading ? (
        <p className="table-empty">Loading…</p>
      ) : connections.length === 0 ? (
        <p className="text-thin-light-gray text-sm py-4 text-center">No Connections yet</p>
      ) : (
        <ul className="w-full">
          {connections.map((c, i) => {
            const name = `${c.first_name} ${c.last_name}`.trim() || c.email;
            return (
              <li
                key={c.id}
                className={`flex justify-between items-center py-3.5 ${
                  i === connections.length - 1 ? "" : "border-b dark:border-[#5356fb29] border-light-purple"
                }`}
              >
                <div className="min-w-0">
                  <p className="text-base text-dark-gray dark:text-white truncate">{name}</p>
                  <p className="text-xs text-thin-light-gray">{formatConnectionDate(c.connected_at)}</p>
                </div>
                {togglingId === c.id ? (
                  <span className="text-sm text-thin-light-gray shrink-0 ml-4">Saving…</span>
                ) : (
                  <SwitchCom value={!c.suspended} handler={() => toggleSuspend(c)} />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {connectionsError ? <p className="text-red-700 mt-4">{connectionsError}</p> : null}
    </div>
  );
}
